/**
 * ConfiguracionEditores — sub-editores reutilizables de Configuración FASE 2.
 *
 * Exporta 4 editores reutilizables que se montan como acordeones dentro de
 * ConfiguracionConstructor (DEC-021 · una sola pantalla con 6 acordeones).
 *
 * Endpoints: /api/configuracion-fase2/{...}
 */
import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Trash2, Save, Loader2, Upload } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/configuracion-fase2`;

const authAxios = axios.create();
authAxios.interceptors.request.use((cfg) => {
  const token = localStorage.getItem("mobilsafe_token");
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

// ============ CATÁLOGO ============

// Iter52.56 · A4 · El catálogo de productos se gestiona SOLO en
// Operaciones SaaS → Administración → Catálogo. Aquí queda en modo
// consulta: las ofertas siguen leyendo el catálogo igual (la oferta
// guarda su propio snapshot de precio, sin cambios).
export function CatalogoEditor() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtroTipo, setFiltroTipo] = useState("");
  useEffect(() => { load(); }, []);
  async function load() {
    setLoading(true);
    try {
      const r = await authAxios.get(`${API}/catalogo`);
      setItems(r.data || []);
    } catch { toast.error("Error cargando catálogo"); }
    setLoading(false);
  }
  const filtrados = filtroTipo ? items.filter((p) => p.tipo === filtroTipo) : items;
  return (
    <div>
      <div className="mb-4 rounded-xl border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900" data-testid="aviso-catalogo-solo-lectura">
        <b>Solo lectura.</b> El catálogo se gestiona en Operaciones SaaS → Administración → Catálogo.
        <a href="/admin/saas/admin-saas#catalogo" className="ml-2 underline font-bold" data-testid="link-catalogo-operaciones">Ir al catálogo</a>
      </div>
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <select value={filtroTipo} onChange={(e) => setFiltroTipo(e.target.value)} className="px-3 py-2 border rounded text-sm bg-white" data-testid="catalogo-filtro">
          <option value="">Todos los tipos</option>
          <option value="hw">HW</option>
          <option value="saas">SaaS</option>
          <option value="instalacion">Instalación</option>
          <option value="servicio">Servicio</option>
        </select>
        <span className="text-xs text-slate-500">{filtrados.length} productos</span>
      </div>
      {loading ? <Loader2 className="w-5 h-5 animate-spin text-slate-400" /> : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs text-slate-500 uppercase border-b">
              <tr><th className="text-left p-2">SKU</th><th className="text-left p-2">Nombre</th><th className="text-left p-2">Tipo</th><th className="text-left p-2">Categoría</th><th className="text-right p-2">Coste€</th><th className="text-right p-2">Precio€</th><th className="text-left p-2">Estado</th></tr>
            </thead>
            <tbody>
              {filtrados.map((p) => (
                <tr key={p.id} className="border-b" data-testid={`catalogo-row-${p.id}`}>
                  <td className="p-2 font-mono text-xs">{p.sku}</td>
                  <td className="p-2">{p.nombre}</td>
                  <td className="p-2 uppercase text-xs">{p.tipo}</td>
                  <td className="p-2">{p.categoria || "—"}</td>
                  <td className="p-2 text-right tabular-nums">{Number(p.coste ?? 0).toFixed(2)}</td>
                  <td className="p-2 text-right tabular-nums">{Number(p.precio ?? 0).toFixed(2)}</td>
                  <td className="p-2">{p.activo === false ? <span className="text-slate-500">Retirado</span> : <span className="text-emerald-700 font-semibold">Activo</span>}</td>
                </tr>
              ))}
              {filtrados.length === 0 && <tr><td colSpan={7} className="text-center p-6 text-slate-400">Sin productos.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ============ CONDICIONES ============

export function CondicionesEditor() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => { load(); }, []);
  async function load() {
    setLoading(true);
    try { const r = await authAxios.get(`${API}/condiciones`); setItems(r.data || []); }
    catch { toast.error("Error cargando condiciones"); }
    setLoading(false);
  }
  async function crear() {
    try {
      const r = await authAxios.post(`${API}/condiciones`, { nombre: "Nueva condición", tipo: "descuento_hw", valor: 0, unidad: "%", orden: items.length + 1 });
      setItems((a) => [...a, r.data]);
    } catch { toast.error("Error creando"); }
  }
  async function actualizar(c) {
    try {
      await authAxios.put(`${API}/condiciones/${c.id}`, { nombre: c.nombre, tipo: c.tipo, valor: +c.valor, unidad: c.unidad, descripcion: c.descripcion, activo: c.activo, orden: c.orden });
      toast.success("Guardada");
    } catch { toast.error("Error"); }
  }
  async function eliminar(id) {
    if (!confirm("¿Eliminar?")) return;
    try { await authAxios.delete(`${API}/condiciones/${id}`); setItems((a) => a.filter((x) => x.id !== id)); }
    catch { toast.error("Error"); }
  }

  if (loading) return <Loader2 className="w-5 h-5 animate-spin text-slate-400" />;
  return (
    <div className="space-y-2">
      {items.map((c) => (
        <div key={c.id} data-testid={`condicion-row-${c.id}`} className="grid grid-cols-12 gap-2 items-end">
          <input className="col-span-3 px-2 py-1.5 border rounded text-sm" value={c.nombre} onChange={(e) => setItems((a) => a.map((x) => x.id === c.id ? { ...x, nombre: e.target.value } : x))} />
          <select className="col-span-3 px-2 py-1.5 border rounded text-sm bg-white" value={c.tipo} onChange={(e) => setItems((a) => a.map((x) => x.id === c.id ? { ...x, tipo: e.target.value } : x))}>
            <option value="descuento_hw">Descuento HW</option>
            <option value="descuento_saas">Descuento SaaS</option>
            <option value="comision_caes">Comisión CAES</option>
            <option value="permanencia">Permanencia</option>
            <option value="modalidad">Modalidad</option>
          </select>
          <input type="number" className="col-span-2 px-2 py-1.5 border rounded text-sm" value={c.valor ?? 0} onChange={(e) => setItems((a) => a.map((x) => x.id === c.id ? { ...x, valor: e.target.value } : x))} />
          <input className="col-span-1 px-2 py-1.5 border rounded text-sm" value={c.unidad || ""} onChange={(e) => setItems((a) => a.map((x) => x.id === c.id ? { ...x, unidad: e.target.value } : x))} />
          <input className="col-span-2 px-2 py-1.5 border rounded text-sm" placeholder="descripción" value={c.descripcion || ""} onChange={(e) => setItems((a) => a.map((x) => x.id === c.id ? { ...x, descripcion: e.target.value } : x))} />
          <div className="col-span-1 flex gap-1">
            <button onClick={() => actualizar(c)} className="p-1.5 text-emerald-600 hover:bg-emerald-50 rounded"><Save className="w-4 h-4" /></button>
            <button onClick={() => eliminar(c.id)} className="p-1.5 text-rose-600 hover:bg-rose-50 rounded"><Trash2 className="w-4 h-4" /></button>
          </div>
        </div>
      ))}
      <button onClick={crear} data-testid="condicion-nueva-btn" className="text-sm px-3 py-2 border rounded hover:bg-slate-50 flex items-center gap-1"><Plus className="w-4 h-4" /> Nueva condición</button>
    </div>
  );
}

// ============ CUESTIONARIO ============

export function CuestionarioEditor() {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => { (async () => {
    try { const r = await authAxios.get(`${API}/cuestionario`); setCfg(r.data); } catch { toast.error("Error"); }
    setLoading(false);
  })(); }, []);

  async function guardar() {
    setSaving(true);
    try {
      await authAxios.put(`${API}/cuestionario`, cfg);
      toast.success("Cuestionario guardado");
    } catch { toast.error("Error guardando"); }
    setSaving(false);
  }

  if (loading || !cfg) return <Loader2 className="w-5 h-5 animate-spin text-slate-400" />;

  const grupos = cfg.preguntas.reduce((acc, p) => ((acc[p.eje] = acc[p.eje] || []).push(p), acc), {});

  function setPregunta(idx, patch) {
    setCfg((c) => ({ ...c, preguntas: c.preguntas.map((p, i) => i === idx ? { ...p, ...patch } : p) }));
  }

  return (
    <div>
      <div className="flex justify-between mb-3 sticky top-16 bg-white py-2 z-10">
        <p className="text-sm text-slate-600">{cfg.preguntas.length} preguntas en {Object.keys(grupos).length} ejes</p>
        <button onClick={guardar} disabled={saving} data-testid="cuest-guardar-btn" className="text-sm px-3 py-1.5 bg-emerald-600 text-white rounded hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-1">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Guardar cuestionario
        </button>
      </div>
      {Object.entries(grupos).map(([eje, preguntas]) => (
        <div key={eje} className="mb-4">
          <h4 className="font-semibold text-slate-700 capitalize mb-2">{eje}</h4>
          <div className="space-y-3">
            {preguntas.map((p) => {
              const idx = cfg.preguntas.findIndex((x) => x.id === p.id);
              const debilidades = new Set(p.opciones_debilidad || []);
              const toggleDebilidad = (opcion) => {
                const next = new Set(debilidades);
                next.has(opcion) ? next.delete(opcion) : next.add(opcion);
                setPregunta(idx, { opciones_debilidad: Array.from(next) });
              };
              return (
                <div key={p.id} className="border border-slate-200 rounded-lg p-2.5 bg-white" data-testid={`pregunta-${p.id}`}>
                  <div className="grid grid-cols-12 gap-2 items-start">
                    <code className="col-span-1 text-xs text-slate-500 pt-2">{p.id}</code>
                    <input className="col-span-7 px-2 py-1.5 border rounded text-sm" value={p.texto} onChange={(e) => setPregunta(idx, { texto: e.target.value })} />
                    <input className="col-span-3 px-2 py-1.5 border rounded text-sm" placeholder="opción1, opción2…" value={(p.opciones || []).join(", ")} onChange={(e) => setPregunta(idx, { opciones: e.target.value.split(",").map((x) => x.trim()).filter(Boolean) })} />
                    <label className="col-span-1 text-xs flex items-center gap-1 pt-2">
                      <input type="checkbox" checked={p.activa !== false} onChange={(e) => setPregunta(idx, { activa: e.target.checked })} /> activa
                    </label>
                  </div>
                  {/* BACKLOG-009 (IMPL-032) · toggle de debilidad por opción */}
                  {(p.opciones || []).length > 0 && (
                    <div className="mt-2 pl-[8.33%] flex flex-wrap items-center gap-1.5">
                      <span className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mr-1">
                        debilidad:
                      </span>
                      {(p.opciones || []).map((opcion, optIdx) => {
                        const es = debilidades.has(opcion);
                        return (
                          <button
                            key={optIdx}
                            type="button"
                            onClick={() => toggleDebilidad(opcion)}
                            data-testid={`debilidad-toggle-${p.id}-${optIdx}`}
                            className={`px-2 py-0.5 rounded-full text-[10px] font-medium border transition ${
                              es
                                ? "bg-rose-100 border-rose-400 text-rose-700"
                                : "bg-slate-50 border-slate-200 text-slate-500 hover:border-rose-300 hover:text-rose-600"
                            }`}
                          >
                            {es ? "● " : ""}{opcion}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

// ============ IA ============

export function IAEditor() {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [docNuevo, setDocNuevo] = useState({ nombre: "", tipo: "guia", contenido: "" });
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadNombre, setUploadNombre] = useState("");
  const [uploadTipo, setUploadTipo] = useState("guia");
  const [subiendo, setSubiendo] = useState(false);

  useEffect(() => { (async () => {
    try { const r = await authAxios.get(`${API}/ia`); setCfg(r.data); } catch { toast.error("Error"); }
    setLoading(false);
  })(); }, []);

  async function guardarPrompts() {
    setSaving(true);
    try { await authAxios.put(`${API}/ia`, cfg); toast.success("Config IA guardada"); }
    catch { toast.error("Error"); }
    setSaving(false);
  }
  async function addDoc() {
    if (!docNuevo.nombre || !docNuevo.contenido) { toast.error("Nombre y contenido obligatorios"); return; }
    try {
      const r = await authAxios.post(`${API}/ia/documentos`, docNuevo);
      setCfg(r.data);
      setDocNuevo({ nombre: "", tipo: "guia", contenido: "" });
      toast.success("Documento añadido");
    } catch { toast.error("Error"); }
  }
  async function subirArchivo() {
    if (!uploadFile) { toast.error("Selecciona un archivo"); return; }
    if (!uploadNombre.trim()) { toast.error("Introduce un nombre"); return; }
    if (uploadFile.size > 2 * 1024 * 1024) { toast.error("Máximo 2 MB por archivo"); return; }
    setSubiendo(true);
    try {
      const fd = new FormData();
      fd.append("archivo", uploadFile);
      fd.append("nombre", uploadNombre);
      fd.append("tipo", uploadTipo);
      const r = await authAxios.post(`${API}/ia/documentos/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setCfg(r.data);
      setUploadFile(null);
      setUploadNombre("");
      setUploadTipo("guia");
      // reset input file
      const el = document.getElementById("ia-upload-input");
      if (el) el.value = "";
      toast.success("Documento subido y procesado");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error subiendo archivo");
    }
    setSubiendo(false);
  }
  async function delDoc(id) {
    if (!window.confirm("¿Eliminar?")) return;
    try { const r = await authAxios.delete(`${API}/ia/documentos/${id}`); setCfg(r.data); }
    catch { toast.error("Error"); }
  }

  if (loading || !cfg) return <Loader2 className="w-5 h-5 animate-spin text-slate-400" />;

  return (
    <div className="space-y-5">
      <div>
        <label className="block text-xs font-medium text-slate-600 mb-1">Modelo preferido</label>
        <input className="w-full px-3 py-2 border rounded text-sm" value={cfg.modelo_preferido} onChange={(e) => setCfg({ ...cfg, modelo_preferido: e.target.value })} />
      </div>
      <div>
        <label className="block text-xs font-medium text-slate-600 mb-1 flex items-center justify-between">
          <span>Prompt base · Diagnóstico consultivo</span>
          <span className="text-[10px] text-slate-400 font-normal">
            {(cfg.prompt_diagnostico_base || "").length} chars · acepta <code>{"{empresa}"}</code> y <code>{"{sector}"}</code>
          </span>
        </label>
        <textarea data-testid="ia-prompt-diagnostico" rows={6} className="w-full px-3 py-2 border rounded text-sm font-mono" value={cfg.prompt_diagnostico_base} onChange={(e) => setCfg({ ...cfg, prompt_diagnostico_base: e.target.value })} />
        <p className="text-[10px] text-slate-400 mt-1">
          Al guardar, este prompt pasa a ser la fuente de verdad del sistema. Si se deja vacío, la próxima
          petición de diagnóstico recarga automáticamente el fallback por defecto.
        </p>
      </div>
      <div>
        <label className="block text-xs font-medium text-slate-600 mb-1">Prompt base · Generación de oferta</label>
        <textarea rows={4} className="w-full px-3 py-2 border rounded text-sm font-mono" value={cfg.prompt_oferta_base} onChange={(e) => setCfg({ ...cfg, prompt_oferta_base: e.target.value })} />
      </div>
      <button onClick={guardarPrompts} disabled={saving} data-testid="ia-guardar-btn" className="text-sm px-4 py-2 bg-emerald-600 text-white rounded hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-2">
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Guardar prompts
      </button>

      <hr />

      <div>
        <h4 className="font-semibold mb-3 text-slate-800">Documentos plantilla (DEC-009)</h4>
        <p className="text-xs text-slate-500 mb-3">
          Estos documentos se inyectan como contexto en cada diagnóstico IA. Solo se usan los marcados como <em>activo</em>.
        </p>
        {(cfg.documentos || []).map((d) => (
          <div key={d.id} className="flex items-center justify-between p-3 border rounded mb-2" data-testid={`doc-${d.id}`}>
            <div className="flex-1 min-w-0">
              <p className="font-medium">{d.nombre} <span className="text-xs text-slate-500">· {d.tipo}</span> <span className="text-xs text-slate-400">· {(d.contenido || "").length} chars</span></p>
              <p className="text-xs text-slate-500 line-clamp-2">{d.contenido}</p>
            </div>
            <button onClick={() => delDoc(d.id)} data-testid={`doc-del-${d.id}`} className="p-2 text-rose-600 hover:bg-rose-50 rounded"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
        {(cfg.documentos || []).length === 0 && <p className="text-sm text-slate-400 italic">Sin documentos.</p>}
      </div>

      {/* Subida de archivo (PDF · DOCX · TXT) */}
      <div className="bg-violet-50 border border-violet-200 p-4 rounded-lg space-y-2" data-testid="ia-upload-panel">
        <p className="font-medium text-sm text-violet-900 flex items-center gap-1">
          <Upload className="w-4 h-4" /> Subir archivo (.pdf · .docx · .txt · máx. 2 MB)
        </p>
        <div className="grid grid-cols-12 gap-2">
          <input
            id="ia-upload-input"
            type="file"
            accept=".pdf,.docx,.txt"
            data-testid="ia-upload-file"
            onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
            className="col-span-12 md:col-span-5 text-xs"
          />
          <input
            data-testid="ia-upload-nombre"
            className="col-span-8 md:col-span-4 px-2 py-1.5 border rounded text-sm"
            placeholder="Nombre del documento"
            value={uploadNombre}
            onChange={(e) => setUploadNombre(e.target.value)}
          />
          <select
            data-testid="ia-upload-tipo"
            className="col-span-4 md:col-span-3 px-2 py-1.5 border rounded text-sm bg-white"
            value={uploadTipo}
            onChange={(e) => setUploadTipo(e.target.value)}
          >
            <option value="guia">Guía</option>
            <option value="argumentario">Argumentario</option>
            <option value="caso_exito">Caso de éxito</option>
          </select>
          <button
            onClick={subirArchivo}
            disabled={subiendo || !uploadFile}
            data-testid="ia-upload-btn"
            className="col-span-12 text-sm px-3 py-2 bg-violet-600 text-white rounded hover:bg-violet-700 disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {subiendo ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            Subir y extraer texto
          </button>
        </div>
        <p className="text-[10px] text-violet-700 mt-1">
          PDFs escaneados (solo imagen) no son compatibles — no hay OCR. Convierte a texto primero.
        </p>
      </div>

      {/* Añadir pegando texto */}
      <div className="bg-slate-50 p-4 rounded-lg space-y-2">
        <p className="font-medium text-sm">… o pegar texto a mano</p>
        <div className="grid grid-cols-12 gap-2">
          <input className="col-span-5 px-2 py-1.5 border rounded text-sm" placeholder="Nombre" value={docNuevo.nombre} onChange={(e) => setDocNuevo({ ...docNuevo, nombre: e.target.value })} />
          <select className="col-span-3 px-2 py-1.5 border rounded text-sm bg-white" value={docNuevo.tipo} onChange={(e) => setDocNuevo({ ...docNuevo, tipo: e.target.value })}>
            <option value="guia">Guía</option><option value="argumentario">Argumentario</option><option value="caso_exito">Caso de éxito</option>
          </select>
          <button onClick={addDoc} data-testid="ia-add-doc-btn" className="col-span-4 text-sm px-3 py-1.5 bg-emerald-600 text-white rounded hover:bg-emerald-700 flex items-center justify-center gap-1">
            <Plus className="w-4 h-4" /> Añadir
          </button>
          <textarea rows={3} className="col-span-12 px-2 py-1.5 border rounded text-sm" placeholder="Contenido (texto plano que se inyecta a la IA)" value={docNuevo.contenido} onChange={(e) => setDocNuevo({ ...docNuevo, contenido: e.target.value })} />
        </div>
      </div>
    </div>
  );
}
